import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import clsx from 'clsx';
import { times } from 'es-toolkit/compat';
import { createContext, use, useCallback, useMemo } from 'react';
import { PiCheckCircleFill } from 'react-icons/pi';
import { Skeleton, SkeletonBlock } from '../Skeleton/index.ts';

const columnStyles = ({
  columns,
  padding,
  singleColumnMobile
}: Pick<BaseProps, 'columns' | 'padding' | 'singleColumnMobile'>) =>
  clsx(
    'grid',

    padding === 'tight' && 'gap-2.5',
    padding === 'normal' && 'gap-4',
    padding === 'extra' && 'gap-6',

    singleColumnMobile
      ? clsx(
          'grid-cols-1',
          columns === 2 && 'sm:grid-cols-2',
          columns === 3 && 'sm:grid-cols-3',
          columns === 4 && 'sm:grid-cols-4',
          columns === 5 && 'sm:grid-cols-5',
          columns === 6 && 'sm:grid-cols-6'
        )
      : clsx(
          columns === 2 && 'grid-cols-2',
          columns === 3 && 'grid-cols-3',
          columns === 4 && 'grid-cols-4',
          columns === 5 && 'grid-cols-5',
          columns === 6 && 'grid-cols-6'
        )
  );

const CardChoiceGroupContext = createContext<
  Pick<CardChoiceGroupProps<string>, 'checkmarkPlacement' | 'showCheckmark' | 'variant'>
>(undefined!);

type BaseProps = React.PropsWithChildren<{
  checkmarkPlacement?: 'top' | 'center' | 'bottom';
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  disabled?: boolean;
  label?: React.ReactNode;
  onBlur?: () => void;
  padding?: 'tight' | 'normal' | 'extra';
  showCheckmark?: boolean;
  singleColumnMobile?: boolean;
}>;

type RadioValueProps<TValue> = BaseProps & {
  variant: 'radio';
  onChange?: (value: TValue) => void;
  value: TValue | null | undefined;
};

type SingleValueProps<TValue> = BaseProps & {
  variant: 'toggle';
  onChange?: (value: TValue) => void;
  value: TValue | null | undefined;
};

type MultipleValueProps<TValue> = BaseProps & {
  variant: 'toggle-multiple';
  onChange?: (value: TValue[]) => void;
  value: TValue[] | undefined;
};

export type CardChoiceGroupProps<TValue> =
  | RadioValueProps<TValue>
  | SingleValueProps<TValue>
  | MultipleValueProps<TValue>;

export function CardChoiceGroup<TValue extends string>({
  children,
  checkmarkPlacement = 'top',
  columns = 1,
  disabled,
  label,
  onBlur,
  onChange,
  padding = 'normal',
  showCheckmark = true,
  singleColumnMobile = true,
  value,
  variant
}: CardChoiceGroupProps<TValue>) {
  const handleChange = useCallback(
    (value: unknown) => {
      if (variant === 'toggle-multiple') {
        onChange?.(value as TValue[]);
      } else if (variant === 'toggle') {
        onChange?.(value instanceof Array ? value[0] : (value as TValue));
      } else {
        onChange?.(value as TValue);
      }
    },
    [onChange, variant]
  );

  const context = useMemo(
    () => ({ checkmarkPlacement, showCheckmark, variant }),
    [checkmarkPlacement, showCheckmark, variant]
  );

  const className = columnStyles({ columns, padding, singleColumnMobile });

  let content: React.ReactNode;
  if (variant === 'radio') {
    content = (
      <RadioGroup
        className={className}
        disabled={disabled}
        onBlur={onBlur}
        onValueChange={handleChange}
        value={value}
      >
        {children}
      </RadioGroup>
    );
  } else if (variant === 'toggle') {
    content = (
      <ToggleGroup
        className={className}
        disabled={disabled}
        onBlur={onBlur}
        onValueChange={handleChange}
        multiple={false}
        value={typeof value !== 'undefined' ? [value ?? ''] : undefined}
      >
        {children}
      </ToggleGroup>
    );
  } else if (variant === 'toggle-multiple') {
    content = (
      <ToggleGroup
        className={className}
        disabled={disabled}
        multiple
        onBlur={onBlur}
        onValueChange={handleChange}
        value={value}
      >
        {children}
      </ToggleGroup>
    );
  } else {
    throw new Error('Invalid variant');
  }

  return (
    <CardChoiceGroupContext value={context}>
      {label ? (
        <div>
          <label
            className={clsx(
              'block text-sm font-medium text-gray-700',
              padding === 'tight' && 'mb-2.5',
              (padding === 'normal' || padding === 'extra') && 'mb-4'
            )}
          >
            {label}
          </label>

          {content}
        </div>
      ) : (
        content
      )}
    </CardChoiceGroupContext>
  );
}

export type CardChoiceGroupOptionProps<TValue extends string> = {
  disabled?: boolean;
  value: TValue;
} & Pick<CardOptionProps, 'children' | 'description' | 'icon' | 'label'>;

export function CardChoiceGroupOption<TValue extends string>({
  disabled,
  value,
  ...optionProps
}: CardChoiceGroupOptionProps<TValue>) {
  const { variant } = use(CardChoiceGroupContext);

  return variant === 'radio' ? (
    <Radio.Root
      disabled={disabled}
      value={value}
      nativeButton
      render={(htmlProps, state) => (
        <CardOption
          {...optionProps}
          checked={state.checked}
          disabled={state.disabled}
          htmlProps={htmlProps}
        />
      )}
    />
  ) : (
    <Toggle
      disabled={disabled}
      value={value}
      nativeButton
      render={(htmlProps, state) => (
        <CardOption
          {...optionProps}
          checked={state.pressed}
          disabled={state.disabled}
          htmlProps={htmlProps}
        />
      )}
    />
  );
}

interface CardOptionProps {
  checked: boolean;
  children?:
    | React.ReactNode
    | ((state: { checked: boolean; disabled: boolean }) => React.ReactNode);
  description?: React.ReactNode;
  disabled: boolean;
  htmlProps: React.ComponentPropsWithoutRef<'button'>;
  icon?: React.ReactNode;
  label?: React.ReactNode;
}

function CardOption({
  checked,
  children,
  disabled,
  description,
  htmlProps,
  icon,
  label
}: CardOptionProps) {
  const { checkmarkPlacement, showCheckmark } = use(CardChoiceGroupContext);

  return (
    <button
      {...htmlProps}
      className={clsx(
        'relative rounded-lg border bg-white py-4 pl-4 text-left shadow-xs focus:outline-hidden',
        disabled && 'opacity-50',
        !disabled && 'cursor-pointer',
        checked ? 'border-transparent' : 'border-gray-300',
        'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        showCheckmark && !children ? 'pr-8' : 'pr-4'
      )}
    >
      {!!children && typeof children === 'function' && children({ checked, disabled })}

      {!!children && typeof children !== 'function' && children}

      {!children && (
        <div className={clsx('flex h-full', !description && 'items-center')}>
          {!!icon && (
            <div className={clsx('flex-none text-gray-800', description ? 'mr-4 p-1' : 'mr-2.5')}>
              <div className={clsx(description ? '*:size-8' : '*:size-6')}>{icon}</div>
            </div>
          )}

          <div className="grow">
            {label && <CardChoiceGroupLabel>{label}</CardChoiceGroupLabel>}

            {description && <CardChoiceGroupDescription>{description}</CardChoiceGroupDescription>}
          </div>
        </div>
      )}

      {showCheckmark && (
        <PiCheckCircleFill
          className={clsx(
            'absolute right-4 size-5 text-primary-600',
            checkmarkPlacement === 'top' && 'top-4',
            checkmarkPlacement === 'center' && 'top-1/2 -translate-y-1/2 transform',
            checkmarkPlacement === 'bottom' && 'bottom-4',
            !checked && 'invisible'
          )}
        />
      )}

      <span
        className={clsx(
          'pointer-events-none absolute -inset-px rounded-lg border-2',
          checked ? 'border-primary-500' : 'border-transparent'
        )}
      />
    </button>
  );
}

export function CardChoiceGroupLabel({ children }: React.PropsWithChildren) {
  return <span className="block text-sm font-medium text-gray-900">{children}</span>;
}

export function CardChoiceGroupDescription({ children }: React.PropsWithChildren) {
  return <span className="mt-1 flex items-center text-sm text-gray-500">{children}</span>;
}

export function CardChoiceGroupSkeleton(
  props: Pick<BaseProps, 'columns' | 'padding' | 'singleColumnMobile'>
) {
  return (
    <Skeleton className={columnStyles(props)}>
      {times(3, i => (
        <SkeletonBlock key={i} className="h-14" />
      ))}
    </Skeleton>
  );
}
